import React, { useEffect } from 'react';
import useToolStore, { TOOL_TYPES } from '../../../store/useToolStore';
import { generateCode } from '../../../utils/codeGenerators';

const DIRECTION_OPTIONS = [
  { value: 'leftToRight', label: 'Left → Right', css: 'to right' },
  { value: 'topToBottom', label: 'Top → Bottom', css: 'to bottom' },
  { value: 'topLeftToBottomRight', label: 'Top-left → Bottom-right', css: 'to bottom right' },
  { value: 'topRightToBottomLeft', label: 'Top-right → Bottom-left', css: 'to bottom left' },
];

const VARIANT_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'tertiary', label: 'Tertiary' },
];

const STYLE_OPTIONS = [
  { value: 'filled', label: 'Filled' },
  { value: 'outline', label: 'Outline' },
  { value: 'ghost', label: 'Ghost' },
];

const SIZE_OPTIONS = [
  { value: 'sm', label: 'S' },
  { value: 'md', label: 'M' },
  { value: 'lg', label: 'L' },
];

const ButtonBuilder = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.BUTTON]);
  const updateConfig = useToolStore((state) => state.updateToolConfig);
  const setGeneratedCode = useToolStore((state) => state.setGeneratedCode);

  useEffect(() => {
    const code = generateCode(TOOL_TYPES.BUTTON, config);
    setGeneratedCode(code);
  }, [config, setGeneratedCode]);

  const set = (patch) => updateConfig(TOOL_TYPES.BUTTON, patch);
  const activeVariantKey =
    config.activeVariant === 'secondary' || config.activeVariant === 'tertiary'
      ? config.activeVariant
      : 'primary';
  const activeVariant = (config.variants && config.variants[activeVariantKey]) || {};

  const setVariantPatch = (variantPatch) =>
    set({
      variants: {
        ...(config.variants || {}),
        [activeVariantKey]: {
          ...(config.variants?.[activeVariantKey] || {}),
          ...variantPatch,
        },
      },
    });

  const controlBaseClassName =
    'w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-w-0 h-10';
  const numberInputClassName =
    `${controlBaseClassName} px-2 sm:px-3 tabular-nums text-right appearance-none`;
  const selectClassName = `${controlBaseClassName} px-2 sm:px-3 text-sm`;
  const segmentedButtonClassName = (isActive) =>
    `w-full px-2 sm:px-3 py-2 rounded-md border text-xs sm:text-sm font-medium transition-colors whitespace-nowrap overflow-hidden text-ellipsis ${isActive
      ? 'bg-gradient-primary text-white border-transparent'
      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`;

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Button Builder</h2>

      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-1 gap-3" style={{ flexDirection: 'column' }}>
          <div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Variant</div>
            <div className="grid grid-cols-3 gap-2">
              {VARIANT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set({ activeVariant: opt.value })}
                  className={segmentedButtonClassName(config.activeVariant === opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Style</div>
            <div className="grid grid-cols-3 gap-2">
              {STYLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set({ activeStyle: opt.value })}
                  className={segmentedButtonClassName(config.activeStyle === opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Size</div>
            <div className="grid grid-cols-3 gap-2">
              {SIZE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set({ activeSize: opt.value })}
                  className={segmentedButtonClassName(config.activeSize === opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', gap: 10, whiteSpace: 'nowrap' }}>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Title</div>
            <input
              type="text"
              value={config.title}
              onChange={(e) => set({ title: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>

          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Text color</div>
            <input
              type="color"
              value={activeVariant.textColor || '#FFFFFF'}
              onChange={(e) => setVariantPatch({ textColor: e.target.value })}
              className="h-11 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">

          <div className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Background</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setVariantPatch({ backgroundMode: 'solid' })}
                className={segmentedButtonClassName((activeVariant.backgroundMode || 'solid') === 'solid')}
              >
                Solid
              </button>
              <button
                type="button"
                onClick={() => setVariantPatch({ backgroundMode: 'gradient' })}
                className={segmentedButtonClassName((activeVariant.backgroundMode || 'solid') === 'gradient')}
              >
                Gradient
              </button>
            </div>
            {(activeVariant.backgroundMode || 'solid') === 'solid' ? (
              <div className="mt-3">
                <input
                  type="color"
                  value={activeVariant.backgroundColor || '#3B82F6'}
                  onChange={(e) => setVariantPatch({ backgroundColor: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                />
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Start</div>
                  <input
                    type="color"
                    value={activeVariant.gradientStartColor || '#3B82F6'}
                    onChange={(e) => setVariantPatch({ gradientStartColor: e.target.value })}
                    className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">End</div>
                  <input
                    type="color"
                    value={activeVariant.gradientEndColor || '#22C55E'}
                    onChange={(e) => setVariantPatch({ gradientEndColor: e.target.value })}
                    className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Direction</div>
                  <div className="relative">
                    <select
                      value={activeVariant.gradientDirection || 'leftToRight'}
                      onChange={(e) => setVariantPatch({ gradientDirection: e.target.value })}
                      className={`${selectClassName} appearance-none pr-10`}
                    >
                      {DIRECTION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <svg
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
          <label>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Padding V</div>
            <input
              type="number"
              step="1"
              min="0"
              value={config.paddingV}
              onChange={(e) => set({ paddingV: Number(e.target.value) })}
              className={numberInputClassName}
              style={{ width: 100, textAlign: 'center', }}
            />
          </label>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Padding H</div>
            <input
              type="number"
              step="1"
              min="0"
              value={config.paddingH}
              onChange={(e) => set({ paddingH: Number(e.target.value) })}
              className={numberInputClassName}
              style={{ width: 100, textAlign: 'center', }}
            />
          </label>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Corner radius</div>
            <input
              type="number"
              step="1"
              min="0"
              value={config.cornerRadius}
              onChange={(e) => set({ cornerRadius: Number(e.target.value) })}
              className={numberInputClassName}
              style={{ width: 100, textAlign: 'center', }}
            />
          </label>
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.borderEnabled}
              onChange={(e) => set({ borderEnabled: e.target.checked })}
            />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Border</span>
          </label>
          {config.borderEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Width</div>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={config.borderWidth}
                  onChange={(e) => set({ borderWidth: Number(e.target.value) })}
                  className={`${numberInputClassName} dark:bg-gray-900`}
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Color</div>
                <input
                  type="color"
                  value={activeVariant.borderColor || '#3B82F6'}
                  onChange={(e) => setVariantPatch({ borderColor: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </label>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.shadowEnabled}
              onChange={(e) => set({ shadowEnabled: e.target.checked })}
            />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Shadow</span>
          </label>
          {config.shadowEnabled && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Color</div>
                <input
                  type="color"
                  value={activeVariant.shadowColor || '#000000'}
                  onChange={(e) => setVariantPatch({ shadowColor: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Opacity</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={config.shadowOpacity}
                  onChange={(e) => set({ shadowOpacity: Number(e.target.value) })}
                  className={`${numberInputClassName} dark:bg-gray-900`}
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Radius</div>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={config.shadowRadius}
                  onChange={(e) => set({ shadowRadius: Number(e.target.value) })}
                  className={`${numberInputClassName} dark:bg-gray-900`}
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">X</div>
                <input
                  type="number"
                  step="1"
                  value={config.shadowX}
                  onChange={(e) => set({ shadowX: Number(e.target.value) })}
                  className={`${numberInputClassName} dark:bg-gray-900`}
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Y</div>
                <input
                  type="number"
                  step="1"
                  value={config.shadowY}
                  onChange={(e) => set({ shadowY: Number(e.target.value) })}
                  className={`${numberInputClassName} dark:bg-gray-900`}
                />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ButtonPreviewComponent = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.BUTTON]);

  const activeVariantKey =
    config.activeVariant === 'secondary' || config.activeVariant === 'tertiary'
      ? config.activeVariant
      : 'primary';
  const activeVariant = (config.variants && config.variants[activeVariantKey]) || {};

  const activeStyle =
    config.activeStyle === 'outline' || config.activeStyle === 'ghost' ? config.activeStyle : 'filled';
  const activeSize = config.activeSize === 'sm' || config.activeSize === 'lg' ? config.activeSize : 'md';

  const sizePreset =
    activeSize === 'sm'
      ? { paddingV: 8, paddingH: 12, fontSize: 14 }
      : activeSize === 'lg'
        ? { paddingV: 14, paddingH: 20, fontSize: 18 }
        : { paddingV: 12, paddingH: 16, fontSize: 16 };

  const getCssGradient = () => {
    const dir =
      DIRECTION_OPTIONS.find((d) => d.value === (activeVariant.gradientDirection || 'leftToRight'))?.css ||
      'to right';
    return `linear-gradient(${dir}, ${activeVariant.gradientStartColor}, ${activeVariant.gradientEndColor})`;
  };

  const hexToRgb = (hex) => {
    const raw = (hex || '').trim().replace('#', '');
    if (raw.length === 3) {
      const r = parseInt(raw[0] + raw[0], 16);
      const g = parseInt(raw[1] + raw[1], 16);
      const b = parseInt(raw[2] + raw[2], 16);
      return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b) ? null : { r, g, b };
    }
    if (raw.length === 6) {
      const r = parseInt(raw.slice(0, 2), 16);
      const g = parseInt(raw.slice(2, 4), 16);
      const b = parseInt(raw.slice(4, 6), 16);
      return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b) ? null : { r, g, b };
    }
    return null;
  };

  const shadowRgb = hexToRgb(activeVariant.shadowColor);
  const shadowRgba = shadowRgb
    ? `rgba(${shadowRgb.r}, ${shadowRgb.g}, ${shadowRgb.b}, ${config.shadowOpacity})`
    : `rgba(0, 0, 0, ${config.shadowOpacity})`;

  const resolvedBorderColor = activeVariant.borderColor || '#3B82F6';
  const resolvedTextColor = activeVariant.textColor || '#FFFFFF';

  const backgroundMode = (activeVariant.backgroundMode || 'solid') === 'gradient' ? 'gradient' : 'solid';
  const filledBackground =
    backgroundMode === 'gradient'
      ? { background: getCssGradient() }
      : { backgroundColor: activeVariant.backgroundColor || '#3B82F6' };

  const styleBackground =
    activeStyle === 'filled'
      ? filledBackground
      : { backgroundColor: 'transparent', background: 'transparent' };

  const borderEnabledByStyle = activeStyle === 'outline' ? true : config.borderEnabled;
  const borderStyle =
    activeStyle === 'ghost'
      ? 'none'
      : borderEnabledByStyle
        ? `${config.borderWidth}px solid ${resolvedBorderColor}`
        : 'none';

  const textColorByStyle = activeStyle === 'filled' ? resolvedTextColor : resolvedBorderColor;

  const style = {
    ...styleBackground,
    color: textColorByStyle,
    padding: `${sizePreset.paddingV}px ${sizePreset.paddingH}px`,
    fontSize: `${sizePreset.fontSize}px`,
    borderRadius: `${config.cornerRadius}px`,
    border: borderStyle,
    boxShadow: config.shadowEnabled
      ? `${config.shadowX}px ${config.shadowY}px ${config.shadowRadius}px ${shadowRgba}`
      : 'none',
  };

  return (
    <div className="h-full w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <button style={style} className="font-semibold select-none">
        {config.title || 'Button'}
      </button>
    </div>
  );
};

export default ButtonBuilder;

